/**
 * AroundAreaChips (spec §7) — the cached-places digest.
 *
 * Category chips with count and median rating, deduped by place identity
 * (placesDigest does the dedupe — the cache returns the same place from
 * several radius queries). No map in v1: the Google key is unset and a
 * blank tile is worse than no map; coordinates are deliberately unused
 * (spec §11 — the map slot is additive once a key is provisioned).
 */
import { areaXRayT, type AreaXRayCopyKey, type AreaXRayLocale } from './copy'
import type { PlaceCategoryDigest } from './placesDigest'

interface AroundAreaChipsProps {
  digest: PlaceCategoryDigest[]
  locale: AreaXRayLocale
}

export function AroundAreaChips({ digest, locale }: AroundAreaChipsProps) {
  return (
    <section data-testid="around-area" aria-label={areaXRayT('aroundEyebrow', locale)}>
      <p style={{ margin: 0, fontFamily: 'var(--lc-font-ui)', fontWeight: 700, color: 'var(--lc-text-heading)' }}>
        {areaXRayT('aroundEyebrow', locale)}
      </p>
      <p style={{ margin: '2px 0 10px', fontFamily: 'var(--lc-font-ui)', color: 'var(--lc-text-muted)', fontSize: 12 }}>
        {areaXRayT('aroundBasis', locale)}
      </p>
      {digest.length === 0 ? (
        <p style={{ fontFamily: 'var(--lc-font-ui)', color: 'var(--lc-text-muted)' }}>
          {areaXRayT('placesEmpty', locale)}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {digest.map((entry) => (
            <li
              key={entry.category}
              data-testid={`chip-${entry.category}`}
              style={{
                fontFamily: 'var(--lc-font-ui)',
                border: '1px solid var(--lc-border)',
                borderRadius: 999,
                padding: '4px 12px',
                background: 'var(--lc-surface-raised)',
                color: 'var(--lc-text-primary)',
              }}
            >
              {areaXRayT(`category_${entry.category}` as AreaXRayCopyKey, locale)}
              {' '}
              <strong>{entry.count}</strong>
              {entry.medianRating != null && (
                <span style={{ color: 'var(--lc-text-muted)' }}>
                  {' '}
                  · {areaXRayT('medianRating', locale, { value: entry.medianRating.toFixed(1) })}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
